import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SecurityIcon from '@mui/icons-material/Security';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetCompaniesQuery, useRequestQuotesMutation } from 'services/api/insuranceQuoteForm.api';
import type { InsuranceQuoteFormData } from 'services/types/insuranceQuoteForm';
import { LIGHT_GREY, NAVY, RADIUS_CARD, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_TINT, YELLOW } from 'styles/tokens';

interface QuoteResultsStepProps {
  formData: InsuranceQuoteFormData;
  businessUnitId: number | null;
  onBack: () => void;
  setIsLoading: (loading: boolean) => void;
}

const QuoteResultsStep: FC<QuoteResultsStepProps> = ({ formData, businessUnitId, onBack, setIsLoading }) => {
  const navigate = useNavigate();
  const [requestError, setRequestError] = useState<string | null>(null);

  const [requestQuotes] = useRequestQuotesMutation();
  const hasValidBusinessUnitId = businessUnitId != null && businessUnitId > 0;
  const { data: companiesData } = useGetCompaniesQuery(hasValidBusinessUnitId ? businessUnitId : 0, {
    skip: !hasValidBusinessUnitId,
  });

  const handleGenerateQuotes = async () => {
    setRequestError(null);
    setIsLoading(true);
    try {
      const result = await requestQuotes({
        rut: formData.formattedRut || formData.rut,
        licensePlate: formData.formattedPlate || formData.licensePlate,
        email: formData.email,
        phone: formData.phone,
        personInfo: {
          firstName: formData.personInfo?.firstName || 'Cliente',
          lastName: formData.personInfo?.lastName || '',
          motherLastName: formData.personInfo?.motherLastName,
        },
        vehicleInfo: formData.vehicleInfo || {},
        ...(businessUnitId && { businessUnitId }),
      }).unwrap();

      if (result.error) {
        setRequestError(result.error);
        return;
      }

      if (result.leadId) {
        navigate(`/broker-dashboard?leadId=${result.leadId}`);
        return;
      }

      setRequestError('No se pudo obtener el ID del lead.');
    } catch (err: unknown) {
      const errorPayload = err as { data?: string | { message?: string; error?: string }; message?: string };
      let errorMessage = 'Error al solicitar cotizaciones.';
      if (typeof errorPayload?.data === 'string') {
        errorMessage = errorPayload.data;
      } else if (errorPayload?.data && typeof errorPayload.data === 'object') {
        errorMessage = errorPayload.data.message || errorPayload.data.error || errorMessage;
      } else if (errorPayload?.message) {
        errorMessage = errorPayload.message;
      }
      setRequestError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* ── Companies section ── */}
      <Box
        sx={{
          borderRadius: RADIUS_CARD,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          border: '1px solid rgba(21, 24, 58, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <SecurityIcon sx={{ color: NAVY, fontSize: '1.3rem' }} />
          <Typography variant="subtitle1" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>
            Aseguradoras
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', mb: 3, fontFamily: 'Jost, sans-serif' }}>
          Se aplicará el descuento configurado en las credenciales de cada aseguradora.
        </Typography>

        {requestError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS_ITEM, fontFamily: 'Jost, sans-serif' }}>
            {requestError}
          </Alert>
        )}
        {!businessUnitId && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS_ITEM, fontFamily: 'Jost, sans-serif' }}>
            Debes seleccionar una unidad de negocio para obtener cotizaciones.
          </Alert>
        )}

        <Grid container spacing={2}>
          {(companiesData ?? []).map((company) => {
            const discountPercent = company.discount != null ? Math.round(company.discount * 100) : 0;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={company.name}>
                <Box
                  sx={{
                    bgcolor: SURFACE_TINT,
                    borderRadius: RADIUS_CARD,
                    p: 2.5,
                    transition: 'background-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(21, 24, 58, 0.06)',
                      boxShadow: SHADOW,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <HourglassEmptyIcon sx={{ color: 'rgba(21, 24, 58, 0.3)' }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}
                    >
                      {company.displayName || company.name}
                    </Typography>
                  </Box>
                  <Tooltip
                    title="Para modificar este descuento edita las credenciales para esta aseguradora en la sección de Credenciales"
                    placement="top"
                  >
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        color: 'rgba(21, 24, 58, 0.5)',
                        cursor: 'help',
                        fontFamily: 'Jost, sans-serif',
                      }}
                    >
                      Descuento: {discountPercent} %
                    </Typography>
                  </Tooltip>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
        <Button
          variant="contained"
          disableElevation
          onClick={onBack}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            bgcolor: LIGHT_GREY,
            color: NAVY,
            borderRadius: RADIUS_COIN,
            px: 3,
            '&:hover': { bgcolor: '#d4d4d4' },
          }}
        >
          Atrás
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => {
            handleGenerateQuotes().catch(() => {});
          }}
          size="medium"
          disabled={
            !businessUnitId ||
            !formData.email?.trim() ||
            !formData.phone?.trim() ||
            !formData.formattedRut ||
            !formData.formattedPlate
          }
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            bgcolor: YELLOW,
            color: NAVY,
            borderRadius: RADIUS_COIN,
            px: 4,
            '&:hover': { bgcolor: '#e5c009' },
            '&.Mui-disabled': { bgcolor: 'rgba(21, 24, 58, 0.08)', color: 'rgba(21, 24, 58, 0.38)' },
          }}
        >
          Generar cotizaciones
        </Button>
      </Box>
    </Box>
  );
};

export default QuoteResultsStep;
